// پارسه — what an AI action costs, and what a پارسه costs: the OFFLINE
// FALLBACK and the first-paint value.
//
// Since 2026-09-12 both numbers come from ONE server-side list: the AI
// service's active pricing policy, served by the BFF as `creditPrices` and read
// here through `src/lib/useCreditPrices.js`
// (docs/AI_COST_AND_PARSEH_PRICING_MANAGEMENT_DESIGN.md §9.2, phase 3). The cost
// chip beside a button, the price list on the credits screen and the "can they
// afford it?" guard all go through `costOf(actionId)` / `costForKind(kind)` /
// `useCreditPrices()` there, so what is advertised follows the policy the
// service actually debits from.
//
// THIS FILE is what renders before that list has arrived and what renders when
// it never does (offline, BFF down). It is NOT where a price is changed — the
// design doc is the reference, and the admin panel's pricing policy is the
// instrument. The BFF carries the same fallback in `src/lib/parsehCost.js` and
// reads the same list.
//
// ── These are ESTIMATES SHOWN BEFORE THE SPEND, not a record of it ────────
//
// The deduction happens inside the AI service after the BFF has handed off, and
// that service's credit check FAILS OPEN — when the wallet is unreachable the
// request is allowed through uncharged (core.md §3.5). So an action can
// legitimately complete having cost nothing, and `CaseChatMessage.costParseh`
// can carry a number the ledger has no row for. That is correct behaviour, and it
// is exactly why nothing in this app ever SUMS these to produce a balance.
// `myWalletBalance` is the only authority.

/** Per-action پارسه costs, keyed by the client action. core.md §8.3.
 *  FALLBACK — read them through `costOf()` in lib/useCreditPrices.js. */
export const PARSEH_COSTS = {
  // پرسش از دستیار — `askAboutCase` / `askInEncounter`, one AskDoctorQuestion.
  ASK: 10,
  // تشخیص افتراقی — ALSO an AskDoctorQuestion carrying a teaching prompt. Not
  // `GetDifferentialDiagnosis`, which is appointment-bound and would answer an
  // empty list for a fake case. Same RPC, same price.
  DIFFERENTIAL: 10,
  // پیشنهاد SOAP — `suggestEncounterSoap`.
  SOAP_SUGGEST: 10,
  // پیشنهاد دستورات — `suggestEncounterOrders`.
  ORDER_SUGGEST: 10,
  // تداخل دارویی — CheckDrugInteraction. Cheaper: a rule engine does most of it.
  INTERACTION: 3,
  // دیکته — ParsePatientVoice. Zero TODAY because the service meters nothing
  // for it; still a Mutation so that metering it later is a price change, not a
  // client rewrite. Change this number and the whole UI follows.
  VOICE: 0,
};

/**
 * The `CaseChatMessage.kind` vocabulary → the cost of the answer that produced
 * it. The BFF's `parsehCost.js` is keyed the same way.
 */
export const COST_BY_KIND = {
  chat: PARSEH_COSTS.ASK,
  differential: PARSEH_COSTS.DIFFERENTIAL,
  interaction: PARSEH_COSTS.INTERACTION,
  soap_suggest: PARSEH_COSTS.SOAP_SUGGEST,
  order_suggest: PARSEH_COSTS.ORDER_SUGGEST,
};

/** 0 for anything not on the price list — a student turn, above all.
 *  FALLBACK — the resolved one is `costForKind()` in lib/useCreditPrices.js. */
export const costForKind = (kind) => COST_BY_KIND[kind] ?? 0;

/**
 * Retail value of one پارسه, in Toman. Used ONLY by the top-up flow.
 * FALLBACK — the policy's `parsehToman` wins when known; read it through
 * `parsehPriceToman()` in lib/useCreditPrices.js.
 *
 * `docs/AI_COST_AND_PARSINA_COIN_PRICING.md` puts the real retail value at
 * 1 پارسه ≈ 1,000 Toman and records that the top-up path currently credits
 * 1 Toman = 1 پارسه with no conversion — a SERVER placeholder this client cannot
 * fix. So this number is what the app CHARGES per پارسه; it is deliberately NOT
 * used to predict the credited balance — the credits screen shows the balance the
 * wallet reports after the callback settles.
 */
export const PARSEH_PRICE_TOMAN = 1000;

/**
 * The price list the credits screen renders — «هر عملیات چند پارسه؟».
 * By price descending, then in the order a student meets them.
 */
// `action` names the PARSEH_COSTS key each row prices, so `resolvedPriceList()`
// in lib/useCreditPrices.js can swap `cost` for the policy's figure row by row.
export const PRICE_LIST = [
  { id: 'ask',          action: 'ASK',           labelKey: 'credits.price.ask',          cost: PARSEH_COSTS.ASK },
  { id: 'differential', action: 'DIFFERENTIAL',  labelKey: 'credits.price.differential', cost: PARSEH_COSTS.DIFFERENTIAL },
  { id: 'soap',         action: 'SOAP_SUGGEST',  labelKey: 'credits.price.soap',         cost: PARSEH_COSTS.SOAP_SUGGEST },
  { id: 'orders',       action: 'ORDER_SUGGEST', labelKey: 'credits.price.orders',       cost: PARSEH_COSTS.ORDER_SUGGEST },
  { id: 'interaction',  action: 'INTERACTION',   labelKey: 'credits.price.interaction',  cost: PARSEH_COSTS.INTERACTION },
  { id: 'voice',        action: 'VOICE',         labelKey: 'credits.price.voice',        cost: PARSEH_COSTS.VOICE },
];

/** Top-up packages, in پارسه. */
export const TOPUP_PACKAGES = [200, 500, 1000];
export const RECOMMENDED_PACKAGE = 500;

/**
 * پارسه as a COUNT, never money-formatted — a پارسه is not a currency.
 * `formatNumber` is optional so the function stays pure and callable outside React.
 */
export function fmtParseh(n, formatNumber) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const rounded = Math.round(Number(n));
  return formatNumber ? formatNumber(rounded) : String(rounded);
}

/**
 * Can this balance pay for that action?
 *
 * `null` means "the balance has not loaded yet" and is deliberately ALLOWED: the
 * server is the real gate (RESOURCE_EXHAUSTED → INSUFFICIENT_CREDITS), and this
 * is only a courtesy that saves a round trip.
 */
export function canAfford(balance, cost) {
  if (balance == null) return true;
  const n = Number(balance);
  if (!Number.isFinite(n)) return true;
  return n >= Number(cost || 0);
}
